import React, { useEffect, useMemo, useState } from "react";
import { Form, useCreateWebhookUrlMutation, WorkflowTriggerType } from "../../../../../__generated__/api";
import SettingsContainer from "../../../../../components/theme/settings";
import FormGroup from "../../../../../components/theme/form-group/FormGroup";
import UserGroups from "../../../../../components/pickers/user-groups/UserGroups";
import CodeBlock from "../../../../../components/code-block/CodeBlock";
import {
    Example, JsonItem, parse, ParsedBodySample,
} from "../Webhooks";
import createTableContainer from "../../../../../components/theme/table/TableContainer";
import Styles from "./GeneralSettings.module.scss";
import { PropertiesByTrigger } from "./useGeneralSettingsProperties";
import GeneralDetailsDescription from "./GeneralDetailsDescription";

type TableItem = JsonItem & { id: string };

const InputBodyTableContainer = createTableContainer<TableItem>();
const InputHeadersTableContainer = createTableContainer<TableItem>();

export default function InboundWebhookGeneralDetails({ properties, formData }:
{ properties: PropertiesByTrigger<WorkflowTriggerType.InboundWebhook>, formData: Form }) {
    const [createWebhookUrl] = useCreateWebhookUrlMutation();
    const [url, setUrl] = useState<string | undefined>();
    const [example, setExample] = useState<ParsedBodySample>();
    const [exampleBody, setExampleBody] = useState<TableItem[]>([]);

    useEffect(() => {
        const firstStep = formData?.workflow?.steps?.firstElement;
        if (!firstStep?.id) {
            return;
        }

        (async () => {
            const webhook = await createWebhookUrl({
                variables: {
                    stepId: firstStep?.id,
                    properties: {
                        isDetached: true,
                        formId: formData?.id,
                    },
                },
            });
            setUrl(webhook.data?.workflowServiceCreateWebhook.url);
        })();
    }, [formData]);

    useEffect(() => {
        if (formData?.exampleInput) {
            const cloned = { ...formData.exampleInput };
            const parsed = parse(cloned as Example);
            setExample(parsed);

            const body = parsed.body.map((row, index) => ({
                ...row,
                id: `body-${index}`,
            }));
            setExampleBody(body as TableItem[]);
        }
    }, [formData?.exampleInput]);

    const inputHeaders = useMemo(() => Object.entries(example?.example.headers ?? {}).map(([key, value]) => ({
        id: key,
        path: [key],
        value,
    })), [example]);

    return (
        <SettingsContainer navigation="none">
            <SettingsContainer.Section title="General">
                <SettingsContainer.Setting
                    title="Name"
                    description="The name of the webhook."
                    permissions={[]}
                >
                    <FormGroup
                        type="text"
                        placeholder="Webhook Name"
                        onChange={evt => properties.name.setValue(evt.target.value)}
                        value={properties.name.value}
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Description"
                    description="The description of the webhook."
                    permissions={[]}
                >
                    <GeneralDetailsDescription properties={properties} />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Groups"
                    description="The groups that will be able to use this action."
                    permissions={[]}
                >
                    <UserGroups
                        value={properties.groups.value}
                        onSelected={properties.groups.setValue}
                        isMulti
                    />
                </SettingsContainer.Setting>

                <SettingsContainer.Setting
                    title="Webhook URL"
                    description="The URL of the webhook."
                    permissions={[]}
                >
                    <CodeBlock
                        isCopyable
                        code={url ?? "Add actions to this automation to generate a webhook URL..."}
                        shouldWrap
                    />
                </SettingsContainer.Setting>

                {
                    inputHeaders.length > 0 && exampleBody.length > 0 ? (
                        <>
                            <SettingsContainer.Setting
                                title="Input headers"
                                description="The input headers of the webhook."
                                permissions={[]}
                            >
                                <InputHeadersTableContainer
                                    data={inputHeaders}
                                    borderVariant="secondary"
                                >
                                    <InputHeadersTableContainer.Table>
                                        <InputHeadersTableContainer.TableValue columnKey="path" title="Path">
                                            {item => item.path.join(".")}
                                        </InputHeadersTableContainer.TableValue>
                                        <InputHeadersTableContainer.TableValue columnKey="value" title="Value">
                                            {item => item.value as string}
                                        </InputHeadersTableContainer.TableValue>
                                    </InputHeadersTableContainer.Table>
                                </InputHeadersTableContainer>
                            </SettingsContainer.Setting>

                            <SettingsContainer.Setting
                                title="Input body"
                                description="The input body of the webhook."
                                permissions={[]}
                            >
                                <InputBodyTableContainer data={exampleBody ?? []} borderVariant="secondary">
                                    <InputBodyTableContainer.Table>
                                        <InputBodyTableContainer.TableValue columnKey="path" title="Path">
                                            {item => item.path.join(".")}
                                        </InputBodyTableContainer.TableValue>
                                        <InputBodyTableContainer.TableValue
                                            columnKey="value"
                                            title="Value"
                                            className={Styles["settings-table-cell"]}
                                        >
                                            {item => item.value as string}
                                        </InputBodyTableContainer.TableValue>
                                    </InputBodyTableContainer.Table>
                                </InputBodyTableContainer>
                            </SettingsContainer.Setting>
                        </>
                    ) : (
                        <SettingsContainer.Setting
                            title="Input headers"
                            description="The input headers of the webhook."
                            permissions={[]}
                        >
                            Send a sample web request to the webhook url above to see input headers and body
                        </SettingsContainer.Setting>
                    )
                }

            </SettingsContainer.Section>
        </SettingsContainer>
    );
}
